/**
 * Shared stats cache with automatic request batching.
 *
 * Solves the N+1 fetch problem where each CountryCard individually
 * requests stats. Instead, requests are batched together and
 * results are cached with TTL.
 */

export interface CountryStats {
  totalVotes: number;
  avgRating: number;
  distribution: Record<number, number>;
}

interface CacheEntry {
  stats: CountryStats;
  fetchedAt: number;
}

type StatsListener = (stats: Record<string, CountryStats>) => void;

const API_BASE = import.meta.env.VITE_SYNC_API_URL ||
  import.meta.env.VITE_PHOTO_API_URL ||
  'https://where-in-the-world-photos.armincerf.workers.dev';

// Cache TTL: 60 seconds (matches backend cache)
const CACHE_TTL_MS = 60_000;

// Batch window: wait this long to collect requests before fetching
const BATCH_WINDOW_MS = 50;

// Max countries per API request
const MAX_BATCH_SIZE = 50;

class StatsCache {
  private cache = new Map<string, CacheEntry>();
  private pendingCodes = new Set<string>();
  private batchTimeout: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Map<string, Set<StatsListener>>();
  private inflight = new Map<string, Promise<CountryStats | null>>();

  /**
   * Get stats for a list of country codes.
   * Returns cached data immediately if available, otherwise queues a fetch.
   */
  async getStats(countryCodes: string[]): Promise<Record<string, CountryStats>> {
    const result: Record<string, CountryStats> = {};
    const toFetch: string[] = [];
    const now = Date.now();

    for (const code of countryCodes) {
      const cached = this.cache.get(code);

      if (cached && now - cached.fetchedAt < CACHE_TTL_MS) {
        // Cache hit
        result[code] = cached.stats;
      } else {
        // Cache miss or expired
        toFetch.push(code);
      }
    }

    if (toFetch.length === 0) {
      return result;
    }

    // Wait for pending requests or queue new ones
    const fetched = await this.fetchBatched(toFetch);
    return { ...result, ...fetched };
  }

  /**
   * Subscribe to stats updates for a country code.
   * Immediately returns cached data if available.
   */
  subscribe(code: string, listener: StatsListener): () => void {
    if (!this.listeners.has(code)) {
      this.listeners.set(code, new Set());
    }
    this.listeners.get(code)!.add(listener);

    // Immediately notify with cached data if available
    const cached = this.cache.get(code);
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      listener({ [code]: cached.stats });
    } else {
      // Queue a fetch
      this.queueFetch(code);
    }

    return () => {
      this.listeners.get(code)?.delete(listener);
    };
  }

  /**
   * Queue a country code for batched fetching.
   */
  private queueFetch(code: string): void {
    this.pendingCodes.add(code);
    this.scheduleBatch();
  }

  /**
   * Schedule a batch fetch if not already scheduled.
   */
  private scheduleBatch(): void {
    if (this.batchTimeout) return;

    this.batchTimeout = setTimeout(() => {
      this.batchTimeout = null;
      this.processBatch();
    }, BATCH_WINDOW_MS);
  }

  /**
   * Process all pending codes as a batch.
   */
  private async processBatch(): Promise<void> {
    if (this.pendingCodes.size === 0) return;

    const codes = Array.from(this.pendingCodes);
    this.pendingCodes.clear();

    // Split into chunks of MAX_BATCH_SIZE
    const chunks: string[][] = [];
    for (let i = 0; i < codes.length; i += MAX_BATCH_SIZE) {
      chunks.push(codes.slice(i, i + MAX_BATCH_SIZE));
    }

    // Fetch all chunks in parallel
    await Promise.all(chunks.map(chunk => this.fetchChunk(chunk)));
  }

  /**
   * Fetch stats for a batch of country codes.
   * Uses request deduplication via inflight map.
   */
  private async fetchBatched(codes: string[]): Promise<Record<string, CountryStats>> {
    const result: Record<string, CountryStats> = {};
    const toFetch: string[] = [];

    // Check for inflight requests
    const inflightPromises: Promise<void>[] = [];

    for (const code of codes) {
      const inflight = this.inflight.get(code);
      if (inflight) {
        // Wait for existing request
        inflightPromises.push(
          inflight.then(stats => {
            if (stats) result[code] = stats;
          })
        );
      } else {
        toFetch.push(code);
      }
    }

    // Wait for inflight requests
    if (inflightPromises.length > 0) {
      await Promise.all(inflightPromises);
    }

    // Fetch remaining codes
    if (toFetch.length > 0) {
      // Split into chunks
      const chunks: string[][] = [];
      for (let i = 0; i < toFetch.length; i += MAX_BATCH_SIZE) {
        chunks.push(toFetch.slice(i, i + MAX_BATCH_SIZE));
      }

      const chunkResults = await Promise.all(chunks.map(chunk => this.fetchChunk(chunk)));
      for (const chunkResult of chunkResults) {
        Object.assign(result, chunkResult);
      }
    }

    return result;
  }

  /**
   * Fetch a single chunk of country codes.
   */
  private async fetchChunk(codes: string[]): Promise<Record<string, CountryStats>> {
    const result: Record<string, CountryStats> = {};

    // Create promise for each code
    const resolvers: Record<string, {
      resolve: (stats: CountryStats | null) => void;
    }> = {};

    for (const code of codes) {
      const promise = new Promise<CountryStats | null>((resolve) => {
        resolvers[code] = { resolve };
      });
      this.inflight.set(code, promise);
    }

    try {
      const url = `${API_BASE}/api/stats?countries=${codes.join(',')}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Stats fetch failed: ${response.status}`);
      }

      const data = await response.json();
      const stats = data.stats || {};
      const now = Date.now();

      for (const code of codes) {
        const countryStats = stats[code] as CountryStats | undefined;

        if (countryStats) {
          // Update cache
          this.cache.set(code, { stats: countryStats, fetchedAt: now });
          result[code] = countryStats;

          // Notify listeners
          const listeners = this.listeners.get(code);
          if (listeners) {
            for (const listener of listeners) {
              listener({ [code]: countryStats });
            }
          }
        }

        // Resolve inflight promise
        resolvers[code]?.resolve(countryStats || null);
      }
    } catch (err) {
      console.error('Failed to fetch stats batch:', err);

      // Resolve all as null
      for (const code of codes) {
        resolvers[code]?.resolve(null);
      }
    } finally {
      // Clean up inflight tracking
      for (const code of codes) {
        this.inflight.delete(code);
      }
    }

    return result;
  }

  /**
   * Invalidate cache for specific codes or all codes.
   */
  invalidate(codes?: string[]): void {
    if (codes) {
      for (const code of codes) {
        this.cache.delete(code);
      }
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get cache statistics for debugging.
   */
  getCacheStats(): {
    size: number;
    pending: number;
    inflight: number;
  } {
    return {
      size: this.cache.size,
      pending: this.pendingCodes.size,
      inflight: this.inflight.size,
    };
  }
}

// Singleton instance
export const statsCache = new StatsCache();
